import React, { useState } from "react"
import { DndContext } from '@dnd-kit/core';
import { SortableContext, arrayMove } from '@dnd-kit/sortable';
import { restrictToParentElement } from '@dnd-kit/modifiers';
import { useCustomSensors } from "./CustomSensors.js";
import { closestCenter } from "@dnd-kit/core";
import { rectSortingStrategy } from "@dnd-kit/sortable";
import "./SortableContainer.css"

/**
 * Wrapper component for a region containing a list of items whose order can be 
 * rearranged by dragging and dropping.
 * 
 * @prop {string} className - the sortable container's css className
 * @prop {string[]} idList - array of unique ids for each draggable item, in the order they appear
 * @prop {Function} setOrder - given a new id array, updates the order of the draggable items
 * @prop {CollisionDetection} collisionDetectionAlgorithm - calculates collisions
 * @prop {SortingStrategy} sortingStrategy - determines how the order of items should change based on where an item is inserted
 * @prop {string} indicatorPadding - css measurement for how much padding should be added to the drop zone indicator
 * @prop {Function} onDrag - function that runs when dragging starts, takes in the ID of the item being dragged
 * @prop {Function} onDrop - function that runs when dragging ends, takes in the ID of the item being dropped and an array of IDs representing the new order
 * @prop {boolean} verticalScrolling - whether vertical scrolling should be allowed when dragging
 * @prop {boolean} horizontalScrolling - whether horizontal scrolling should be allowed when dragging
 */
const SortableContainer = ({
  className="", 
  idList,
  setOrder, 
  collisionDetectionAlgorithm=closestCenter, 
  sortingStrategy=rectSortingStrategy, 
  indicatorPadding="1.5rem",
  onDrag,
  onDrop,
  children,
  verticalScrolling=true,
  horizontalScrolling=false,
}) => {
  const [indicateDropZone, setIndicateDropZone] = useState(false)
  const sensors = useCustomSensors()

  const handleDragStart = async (event) => {
    if (onDrag && event.active?.id) {
      onDrag(event.active.id)
    }
    setIndicateDropZone(true)
  }

  // Pointer capture is set in CustomSensors.js when dragging starts, to keep
  // the cursor from interacting with other elements (e.g. links, buttons)
  // while dragging. The browser releases it automatically on pointerup, but
  // explicitly release it here to handle edge cases (such as canceling with
  // Escape key)
  const releasePointerCapture = (event) => {
    const native = event.activatorEvent
    if (native?.pointerId != null && native.target?.hasPointerCapture?.(native.pointerId)) {
      native.target.releasePointerCapture(native.pointerId)
    }
  }

  const handleDragEnd = async (event) => {
    releasePointerCapture(event)
    const {active, over} = event
    setIndicateDropZone(false)
    
    if (active && over && active.id !== over.id) {
      const oldIndex = idList.indexOf(active.id)
      const newIndex = idList.indexOf(over.id)
      const newOrder = arrayMove(idList, oldIndex, newIndex)
      if (onDrop) {
        onDrop(active.id, newOrder)
      }
      await setOrder(newOrder)
    }
  }

  const handleDragCancel = (event) => {
    releasePointerCapture(event)
    setIndicateDropZone(false)
  }

  return (
    <DndContext
      sensors={sensors}
      collisionDetection={collisionDetectionAlgorithm}
      onDragEnd={handleDragEnd}
      onDragStart={handleDragStart}
      onDragCancel={handleDragCancel}
      autoScroll={{
        threshold: {
          x: horizontalScrolling ? 0.2 : 0,
          y: verticalScrolling ? 0.2 : 0,
        },
        acceleration: 5,
      }}
      modifiers = {[restrictToParentElement]}
      >
      <SortableContext 
        items={idList}
        strategy={sortingStrategy}
      >
        <div className={"sortable-container " + className}>
          <div 
            className="drop-zone-indicator" 
            style={{
              height: `calc(100% + (2 * ${indicatorPadding}))`,
              width: `calc(100% + (2 * ${indicatorPadding}))`,
              display: indicateDropZone ? "block" : "none",
            }}
          />
          {children}
        </div>
      </SortableContext>
    </DndContext>
  )
}

export default SortableContainer
